import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Network, ShieldCheck, Store, Users } from 'lucide-react';
import Badge from '../../ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { cn } from '../../../utils';

export type HierarchyAgent = {
  id: string;
  name: string;
  email?: string | null;
  status?: string | null;
  costRate?: number | string | null;
};

export type HierarchyShop = {
  id: string;
  name: string;
  email?: string | null;
  status?: string | null;
  agentId?: string | null;
  costRate?: number | string | null;
};

type SuperAgentHierarchyChartProps = {
  superAgent: { id: string; name: string; email?: string | null; status?: string | null };
  agents: HierarchyAgent[];
  shops: HierarchyShop[];
};

const rateToPercent = (value: unknown): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0.00%';
  return `${(n * 100).toFixed(2)}%`;
};

const statusClassName = (status?: string | null): string => {
  if (status === 'active') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'suspended') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

function NodeCard({
  title,
  subtitle,
  status,
  meta,
  to,
  tone = 'default',
  icon,
}: {
  title: string;
  subtitle?: string | null;
  status?: string | null;
  meta?: string | null;
  to?: string;
  tone?: 'root' | 'agent' | 'shop' | 'default';
  icon?: React.ReactNode;
}) {
  const toneClass =
    tone === 'root'
      ? 'border-primary/40 bg-primary/5'
      : tone === 'agent'
        ? 'border-sky-200 bg-sky-50/60'
        : tone === 'shop'
          ? 'border-violet-200 bg-violet-50/50'
          : 'border-border bg-card';

  const titleNode = to ? (
    <Link
      to={to}
      className="font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
    >
      {title}
    </Link>
  ) : (
    <span className="font-semibold text-foreground">{title}</span>
  );

  return (
    <div className={cn('rounded-lg border px-3 py-2.5 shadow-sm min-w-[14rem] max-w-[22rem]', toneClass)}>
      <div className="flex items-start gap-2">
        {icon ? <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span> : null}
        <div className="min-w-0 space-y-1">
          <div className="truncate">{titleNode}</div>
          {subtitle ? <p className="text-xs text-muted-foreground truncate">{subtitle}</p> : null}
          <div className="flex flex-wrap items-center gap-1.5">
            {status ? (
              <Badge variant="outline" className={cn('uppercase text-[10px] tracking-wide', statusClassName(status))}>
                {status}
              </Badge>
            ) : null}
            {meta ? <span className="text-[11px] text-muted-foreground tabular-nums">{meta}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Org-style hierarchy chart: Super Agent → Agents → Shops.
 */
export const SuperAgentHierarchyChart: React.FC<SuperAgentHierarchyChartProps> = ({
  superAgent,
  agents,
  shops,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(agents.map((a) => [a.id, true])),
  );

  const shopsByAgent = useMemo(() => {
    const map: Record<string, HierarchyShop[]> = {};
    for (const shop of shops) {
      const key = shop.agentId || '__unassigned__';
      if (!map[key]) map[key] = [];
      map[key].push(shop);
    }
    return map;
  }, [shops]);

  const toggle = (agentId: string) => {
    setExpanded((prev) => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const expandAll = () => {
    setExpanded(Object.fromEntries(agents.map((a) => [a.id, true])));
  };

  const collapseAll = () => {
    setExpanded(Object.fromEntries(agents.map((a) => [a.id, false])));
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between space-y-0">
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle className="text-lg">Hierarchy</CardTitle>
            <p className="text-xs text-muted-foreground font-normal mt-0.5">
              Super agent → agents → shops ({agents.length} agents · {shops.length} shops)
            </p>
          </div>
        </div>
        {agents.length > 0 ? (
          <div className="flex gap-2">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={expandAll}
            >
              Expand all
            </button>
            <button
              type="button"
              className="text-xs font-medium text-muted-foreground hover:underline"
              onClick={collapseAll}
            >
              Collapse all
            </button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <div className="min-w-[20rem] space-y-4 pb-2">
          <NodeCard
            tone="root"
            title={superAgent.name}
            subtitle={superAgent.email}
            status={superAgent.status}
            icon={<ShieldCheck className="h-4 w-4" />}
          />

          {agents.length === 0 ? (
            <p className="pl-6 text-sm text-muted-foreground">No agents under this super agent.</p>
          ) : (
            <ul className="relative ml-4 space-y-4 border-l-2 border-slate-200 pl-6">
              {agents.map((agent) => {
                const childShops = shopsByAgent[agent.id] ?? [];
                const isOpen = expanded[agent.id] !== false;
                return (
                  <li key={agent.id} className="relative space-y-3">
                    <span
                      className="absolute -left-[1.65rem] top-4 h-px w-4 bg-slate-200"
                      aria-hidden
                    />
                    <div className="flex flex-wrap items-start gap-2">
                      <button
                        type="button"
                        className="mt-2 rounded border bg-background p-0.5 text-muted-foreground hover:bg-muted"
                        onClick={() => toggle(agent.id)}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? `Collapse shops for ${agent.name}` : `Expand shops for ${agent.name}`}
                      >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </button>
                      <NodeCard
                        tone="agent"
                        title={agent.name}
                        subtitle={agent.email}
                        status={agent.status}
                        meta={`Cost ${rateToPercent(agent.costRate)} · ${childShops.length} shop${childShops.length === 1 ? '' : 's'}`}
                        to={`/agents/${agent.id}`}
                        icon={<Users className="h-4 w-4" />}
                      />
                    </div>

                    {isOpen ? (
                      childShops.length === 0 ? (
                        <p className="ml-10 text-xs text-muted-foreground">No shops under this agent.</p>
                      ) : (
                        <ul className="relative ml-10 space-y-2 border-l-2 border-violet-100 pl-6">
                          {childShops.map((shop) => (
                            <li key={shop.id} className="relative">
                              <span
                                className="absolute -left-[1.65rem] top-4 h-px w-4 bg-violet-100"
                                aria-hidden
                              />
                              <NodeCard
                                tone="shop"
                                title={shop.name}
                                subtitle={shop.email}
                                status={shop.status}
                                meta={`Cost ${rateToPercent(shop.costRate)}`}
                                to={`/shops/${shop.id}`}
                                icon={<Store className="h-4 w-4" />}
                              />
                            </li>
                          ))}
                        </ul>
                      )
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {(shopsByAgent.__unassigned__?.length ?? 0) > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm">
              <p className="font-medium text-amber-900">Unassigned shops</p>
              <ul className="mt-2 space-y-2">
                {shopsByAgent.__unassigned__.map((shop) => (
                  <li key={shop.id}>
                    <NodeCard
                      tone="shop"
                      title={shop.name}
                      subtitle={shop.email}
                      status={shop.status}
                      to={`/shops/${shop.id}`}
                      icon={<Store className="h-4 w-4" />}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperAgentHierarchyChart;
